'use client'

import _ from 'lodash'
import { useState, useCallback, useEffect } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { usePathname, useSearchParams, useRouter } from 'next/navigation'
import ResponsivePagination from 'react-responsive-pagination'

import { Api } from '@cruz-com/lookup'
import { abbNumber } from '@/lib'

type Props = {
  meta: Api.Meta
  onPageNumberChanged?: (val: any) => void
} & ((React.ComponentPropsWithoutRef<'span'>))

export function Pagination({ meta, onPageNumberChanged }: Props) {
  const router = useRouter()
  const pathname = usePathname()
  const params = useSearchParams()
  const query: any = _.assign({}, _.pick(meta, 'page_number'), Object.fromEntries(params))
  const [currentPage, setCurrentPage] = useState(_.toNumber(query?.page_number) || 1)

  const { total_pages, page_size, page_number, total_records_matched, location } = meta || {}

  useEffect(() => {
    setCurrentPage(_.toNumber(query?.page_number) || 1)
  }, [query?.page_number])

  const handlePageClick = (pageNumber: number) => {
    onPageNumberChanged && onPageNumberChanged(pageNumber)
    setCurrentPage(pageNumber)
    router.push(pathname + '?' + new URLSearchParams({ ...query, page_number: pageNumber }).toString())
  }

  const totalPages = useCallback(() => {
    if (!total_pages) {
      return 0
    }
    const maxPages = Math.floor(10000 / (page_size || 24))
    const x = Math.max(currentPage + 1, Math.min(total_pages, 10))
    if (x < total_pages) {
      return Math.min(x, maxPages)
    }
    return Math.min(total_pages, maxPages)
  }, [total_pages, page_size, currentPage])

  if (_.isEmpty(meta)) {
    return <></>
  }

  return (
    <div className='max-w-[600px] mx-auto pt-10 text-center'>
      <ResponsivePagination
        total={totalPages()}
        current={currentPage}
        onPageChange={handlePageClick}
        previousLabel={<ChevronLeftIcon className='size-6' title='Previous Page' />}
        nextLabel={<ChevronRightIcon className='size-6' title='Next Page' />}
      />
      <div className='text-center mt-6 text-sm/6 font-light'>
        Total <b>{abbNumber(total_records_matched, 2)}</b> results, <b>{total_pages?.toLocaleString()}</b> page{(total_pages || 0) > 1 ? 's' : ''}.
        {
          meta?.location && (location?.distance || 0) > 0 &&
          <div>Searching within <b>{meta?.location?.distance?.toLocaleString()} miles</b> of <b>{location?.city}, {location?.state_abbr}</b></div>
        }
      </div>
      {/* for crawler */}
      <a className='hidden text-center text-sm p-4'
        href={pathname + '?' + new URLSearchParams({
          ...query, ..._.pick(meta, 'sort_value', 'sort_page_number'), page_number: ((page_number || 0) + 1)
        }).toString()}
      >
        Next Page
      </a>
    </div>
  )

}
